import type { RaisingCode } from "./qualifyLead";
import type { Scenario } from "./scenario";
import type { Stage } from "./situation";

/**
 * U4 REPLYFULL (Agent Brains 5.1, 5.3, section 9; design DESIGN-research U4).
 * The sales mode, the draft intents and the warm-up lines the reply prompt
 * carries when ACKRA_REPLY_FULL is on.
 *
 * Everything here is deterministic and constant. The mode is chosen by code
 * from the routed scenario and the RAISING codes of the latest `lead.scored`,
 * never by the model, and never from the band, the lowering codes or the VIP
 * flag: VIP changes service level and routing, never wording of a number or a
 * posture (CLAUDE.md sections 0.5 and 3), and a low band must not make a
 * customer "worded differently" (design: nobody rejected, everybody routed).
 *
 * Every constant line below is pinned offer-free, money-free and wealth-free
 * in salesMode.test.ts (CLAUDE.md section 0.5): service, craft and attention
 * lead. None mentions money, and none names who the customer is.
 */

export const SALES_MODES = [
  "spin",
  "spin_light",
  "closer",
  "closer_e",
  "challenger",
  "heard",
  "repair_triage",
  "none",
] as const;
export type SalesMode = (typeof SALES_MODES)[number];

export const DRAFT_INTENTS = [
  "answer_and_next_step",
  "discovery_question",
  "invite_consult",
  "answer_and_check",
] as const;
export type DraftIntent = (typeof DRAFT_INTENTS)[number];

export const WARMUP_KINDS = ["repair_ack", "inquiry", "custom"] as const;
export type WarmupKind = (typeof WARMUP_KINDS)[number];

/**
 * The mode for one turn. Reads the routed scenario and the raising codes and
 * nothing else; `stage` is part of the contract's shape and deliberately
 * unread in v1 (the design's rules do not use it), so a stage can never move
 * a mode by accident.
 */
export function selectSalesMode(i: {
  readonly scenario: Scenario | "unrouted";
  readonly raising: readonly RaisingCode[];
  readonly stage: Stage;
}): SalesMode {
  const has = (code: RaisingCode): boolean => i.raising.includes(code);
  switch (i.scenario) {
    case "complaint_warranty":
      return "heard";
    case "repair_inquiry":
      return "repair_triage";
    case "educated_buyer_debate":
      return "challenger";
    case "price_challenge":
      // The playcard already says never to offer or imply a discount here.
      return "closer_e";
    case "custom_engagement":
      return has("intent.consult") || has("intent.visit") ? "closer" : "spin";
    case "ready_made_purchase":
      return has("need.evidenced") && has("timeline.evidenced") ? "closer" : "spin_light";
    default:
      return "none";
  }
}

/** Primary intent first, at most three. A mode with one intent has no alternates. */
const INTENTS_BY_MODE: Readonly<Record<SalesMode, readonly [DraftIntent, ...DraftIntent[]]>> = {
  spin: ["discovery_question", "answer_and_next_step", "answer_and_check"],
  spin_light: ["answer_and_next_step", "discovery_question"],
  closer: ["invite_consult", "answer_and_next_step"],
  closer_e: ["answer_and_next_step", "answer_and_check"],
  challenger: ["answer_and_check", "answer_and_next_step"],
  heard: ["answer_and_check"],
  repair_triage: ["answer_and_next_step", "discovery_question"],
  none: ["answer_and_next_step"],
};

export function intentsFor(mode: SalesMode): readonly [DraftIntent, ...DraftIntent[]] {
  return INTENTS_BY_MODE[mode];
}

/** Which warm-up the route earns, or null when the route earns none. */
export function warmupKindFor(scenario: Scenario | "unrouted"): WarmupKind | null {
  switch (scenario) {
    case "repair_inquiry":
      return "repair_ack";
    case "custom_engagement":
      return "custom";
    case "ready_made_purchase":
    case "price_challenge":
    case "educated_buyer_debate":
      return "inquiry";
    default:
      return null;
  }
}

/**
 * Trusted constant lines, one per warm-up kind. Service and care lead; no
 * money, no offer, no wealth (CLAUDE.md section 0.5). TA-1361: a line describes
 * a behaviour and names no phrase for the model to copy, since the model quoted
 * the old ones back as hollow openers, and it claims nothing about where the
 * piece is: the shop has not seen a piece the customer still holds (CLAUDE.md
 * section 9, B14; section 4, precision outranks realism).
 */
export const WARMUP_LINES: Readonly<Record<WarmupKind, string>> = {
  repair_ack: "Open with one plain sentence that shows you read what they told you about their piece and what it needs, in your own words, before any question. Do not say the shop has the piece or has looked at it unless they said so.",
  inquiry: "Open with one plain sentence that picks up what they asked about, in your own words, before any question. No stock phrase, and no praise of a piece you have not seen.",
  custom: "Open with one plain sentence that reflects what they described wanting, in your own words, before any question. No stock phrase about custom work.",
};

/** One trusted constant line per mode (none carries no line). */
export const MODE_LINES: Readonly<Record<Exclude<SalesMode, "none">, string>> = {
  spin: "Learn the situation before steering: after answering what they asked, ask one open question about what they are looking for or the occasion.",
  spin_light: "Answer first. If one thing about the piece or the timing is still unknown, ask for it once, otherwise move to the next step.",
  closer: "They are ready to move. Confirm what you know in a few words and propose a single clear next step: a time to come in or to talk it through.",
  closer_e: "Stay calm and unhurried. Answer only from the facts you have, speak to the piece and the craft behind it, and propose one next step.",
  challenger: "They have done their homework. Respect it, add one point they may not have weighed, and keep it brief and plain.",
  heard: "Acknowledge what happened in your own words first, show you take it seriously, and say what happens next. No defending and no explaining away.",
  repair_triage: "Treat the piece with care in your wording. Find out what is wrong with it and how it is worn, and say what the shop will do next.",
};

/** One trusted constant line per intent. */
export const INTENT_LINES: Readonly<Record<DraftIntent, string>> = {
  answer_and_next_step: "Answer what they asked, then give one clear next step.",
  discovery_question: "Answer what they asked, then ask one question that helps you understand what they want.",
  invite_consult: "Answer what they asked, then invite them to a consult or a visit.",
  answer_and_check: "Answer what they asked from the facts, and say you will check anything you are not sure of.",
};
